import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserGear, ShieldCheck, X } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../../lib/api";

/**
 * Modal-style inline panel: pick a user, view/edit their module access.
 * Supports:
 *   • Assign a custom role (source-of-truth = role.modules)
 *   • Toggle individual modules (source-of-truth = per-user override list)
 *   • Clear both (revert to tenant default)
 */
export default function UserAccessPanel({ tenantModules = [] }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("role");  // "role" | "modules" | "default"
  const [customRoleId, setCustomRoleId] = useState("");
  const [overrideMods, setOverrideMods] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([
        api.get("/tenant/users"),
        api.get("/tenant/custom-roles"),
      ]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = users.find((u) => u.id === selectedId);

  useEffect(() => {
    if (!selected) return;
    if (selected.custom_role_id) {
      setMode("role"); setCustomRoleId(selected.custom_role_id); setOverrideMods(new Set());
    } else if (selected.module_access) {
      setMode("modules"); setCustomRoleId(""); setOverrideMods(new Set(selected.module_access));
    } else {
      setMode("default"); setCustomRoleId(""); setOverrideMods(new Set());
    }
  }, [selected]);

  const toggle = (m) => {
    setOverrideMods((s) => {
      const n = new Set(s); if (n.has(m)) n.delete(m); else n.add(m); return n;
    });
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    let payload = {};
    if (mode === "role" && customRoleId) payload = { custom_role_id: customRoleId };
    else if (mode === "modules") payload = { module_access: [...overrideMods] };
    // "default" → empty payload = clear both
    try {
      const { data } = await api.patch(`/tenant/users/${selected.id}/access`, payload);
      toast.success(`Access updated — ${data.effective_modules.length} module${data.effective_modules.length === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-8 border border-zinc-950" data-testid="user-access-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
        <UserGear size={12} /> USER_ACCESS
        <span className="text-[9px] text-zinc-400 ml-2">Fine-grained module control per teammate</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr]">
        {/* Users list */}
        <div className="border-r border-zinc-200 max-h-[420px] overflow-y-auto">
          {users.map((u) => (
            <button
              key={u.id}
              onClick={() => setSelectedId(u.id)}
              data-testid={`user-select-${u.id}`}
              className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 flex items-center justify-between ${selectedId === u.id ? "bg-zinc-950 text-white hover:bg-zinc-950" : ""}`}
            >
              <div>
                <div className="font-semibold text-sm">{u.name || u.email}</div>
                <div className={`text-[11px] ${selectedId === u.id ? "text-zinc-400" : "text-zinc-500"}`}>{u.email}</div>
              </div>
              <div className="text-right">
                <div className={`font-mono text-[9px] tracking-[0.12em] uppercase ${selectedId === u.id ? "text-zinc-400" : "text-zinc-500"}`}>
                  {u.custom_role_name || (u.module_access ? "CUSTOM_MODS" : u.role?.toUpperCase())}
                </div>
                <div className={`text-[10px] tnum ${selectedId === u.id ? "text-[#FF3E00]" : "text-zinc-600"}`}>
                  {u.effective_modules?.length ?? 0} mods
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Detail pane */}
        <div className="p-5">
          {!selected ? (
            <div className="h-full flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <UserGear size={40} className="text-zinc-200 mx-auto" />
                <div className="mt-3 font-display font-black text-2xl text-zinc-300">Pick a user</div>
                <div className="text-[11px] text-zinc-400 mt-1">Assign a role, custom modules, or the tenant default.</div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">/ SELECTED USER</div>
                  <div className="font-display font-black text-xl">{selected.name || selected.email}</div>
                  <div className="text-[11px] text-zinc-500">{selected.email} · base role: {selected.role}</div>
                </div>
                <button onClick={() => setSelectedId(null)} className="text-zinc-400 hover:text-zinc-950" data-testid="user-detail-close">
                  <X size={16} />
                </button>
              </div>

              <div className="mt-4 flex gap-2">
                {[["role", "CUSTOM ROLE"], ["modules", "CUSTOM MODULES"], ["default", "TENANT DEFAULT"]].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setMode(k)}
                    data-testid={`access-mode-${k}`}
                    className={`font-mono text-[10px] tracking-[0.12em] uppercase border px-3 py-1.5 transition-colors ${mode === k ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}>
                    {label}
                  </button>
                ))}
              </div>

              {mode === "role" && (
                <div className="mt-3">
                  <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">assign role</div>
                  <select
                    data-testid="access-role-select"
                    value={customRoleId}
                    onChange={(e) => setCustomRoleId(e.target.value)}
                    className="mt-2 w-full font-mono text-sm border border-zinc-300 px-3 py-2 outline-none focus:border-zinc-950 bg-white"
                  >
                    <option value="">— select a role —</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name} ({r.modules.join(", ")})</option>
                    ))}
                  </select>
                  {roles.length === 0 && <div className="text-[11px] text-zinc-400 mt-1">No custom roles yet — create one above.</div>}
                </div>
              )}

              {mode === "modules" && (
                <div className="mt-3">
                  <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">pick modules</div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5">
                    {tenantModules.map((m) => {
                      const on = overrideMods.has(m);
                      return (
                        <button
                          key={m}
                          onClick={() => toggle(m)}
                          data-testid={`access-mod-${m}`}
                          className={`font-mono text-[11px] px-3 py-2 border text-left transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-300 hover:border-zinc-950"}`}>
                          <ShieldCheck size={11} weight={on ? "fill" : "regular"} className={`inline mr-1.5 ${on ? "text-[#FF3E00]" : "text-zinc-400"}`} />
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {mode === "default" && (
                <div className="mt-3 border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
                  User will inherit all modules from the tenant subscription. Any custom role or per-user override will be cleared.
                </div>
              )}

              <button
                onClick={save}
                disabled={busy || (mode === "role" && !customRoleId)}
                data-testid="save-access-btn"
                className="mt-4 w-full bg-[#FF3E00] text-white font-mono text-xs tracking-[0.15em] uppercase py-3 hover:bg-zinc-950 disabled:opacity-50 transition-colors">
                {busy ? "SAVING…" : "APPLY CHANGES"}
              </button>

              <div className="mt-4 font-mono text-[10px] tracking-[0.15em] text-zinc-500 uppercase">Currently effective</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(selected.effective_modules || []).map((m) => (
                  <span key={m} className="font-mono text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5">
                    {m}
                  </span>
                ))}
                {(selected.effective_modules || []).length === 0 && (
                  <span className="text-[11px] text-zinc-400">No modules</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
